<template>
  <div id="itemSnoozes">

    <div class="snooze" v-for="(item, itemIndex) of outOfStock">
      <div class="section">
        <div class="icon">
          <svgicon src="icon/inventory.svg" :width="16" :height="16"></svgicon>
        </div>
      </div>
      <div class="section middle grow">
        <div class="section grow">
          <label for="selProductName">{{ $t("item") }}</label>
          <AjaxSelect v-model="outOfStock[itemIndex]" api="/api/entity/orderable/select" :query="query"
            :map-fn="formatNameForSelect" />
        </div>
        <div class="section">
          <label for="selOutOfStock">{{ $t("items.out_of_stock") }}</label>
          <select name="" id="selOutOfStock" v-model="item.outOfStock">
            <option :value="false">{{ $t("item_snooze.snoozing_short") }}</option>
            <option :value="true">{{ $t("item_snooze.oos_short") }}</option>
          </select>
        </div>
        <div class="section" v-if="!item.outOfStock">
          <label for="datStart">{{ $t("start") }}</label>
          <datetime v-model="item.fromTime" :use12-hour="hourFormat == '12'" value-zone="local" type="datetime"
            input-id="s_start" :minute-step="minuteStep" :min-datetime="now"
            :max-datetime="item.toTime" />
        </div>
        <div class="section" v-if="!item.outOfStock">
          <label for="datEnd">{{ $t("end") }}</label>
          <datetime v-model="item.toTime" :use12-hour="hourFormat == '12'" value-zone="local" type="datetime"
            input-id="s_end" :minute-step="minuteStep" :min-datetime="item.fromTime"
            :max-datetime="maxRange" />
        </div>
      </div>
      <div class="section">
        <div class="action" v-if="false">
          <svgicon src="icon/save.svg" :width="16" :height="16"></svgicon>
        </div>
        <div class="action" @click="deleteItem(item)">
          <svgicon src="icon/delete.svg" :width="16" :height="16"></svgicon>
        </div>
      </div>
    </div>

    <div class="addSnooze" @click="addNew">
      <svgicon src="icon/add-circle.svg"></svgicon>
      {{ $t("item_snooze.add_snooze") }}
    </div>

  </div>
</template>

<script>
  import { Core } from "@/core/Core";
  import { DateTime } from "luxon";
  import ItemSnoozeEdit from "@/views/settings/quick/ItemSnoozeEdit";
  import AjaxSelect from "@/components/tools/AjaxSelect.vue";

  export default {
    name: "ItemSnoozes",

    components: { ItemSnoozeEdit, AjaxSelect },

    data() {
      return {
        outOfStock: [],
        originalOutOfStock: [],
        now: DateTime.local().toISO(),
        maxRange: DateTime.local().plus({ days: 30 }).toISO(),
        dateFormat: 'yyyy-MM-dd HH:mm:ss',
        minuteStep: 15
      }
    },

    computed: {
      inventoryId() {
        return this.$store.state.contextInventory ? this.$store.state.contextInventory.id : null;
      },
      companyId() {
        return this.$store.state.contextCompany ? this.$store.state.contextCompany.id : null;
      },
      companyNameCanonical() {
        return this.$store.state.contextCompany ? this.$store.state.contextCompany.nameCanonical : null;
      },
      hourFormat() {
        if (this.$store.state.contextCompany) {
          return this.$store.state.contextCompany.hourFormat;
        }
        return "24";
      },
      formatNameForSelect() {
        return item => {
          if (item.itemType) {
            item.type = "item";
          } else if (item.variantType) {
            item.type = "variant";
          } else if (item.modifierType) {
            item.type = "modifier";
          } else if (item.comboType) {
            item.type = "combo";
          } else if (item.supplementType) {
            item.type = "supplement";
          } else if (item.customType) {
            item.type = "custom";
          }
          return `${this.$to(item.name)} (${this.$t(`item_type.${item.type}`)})`;
        }
      },
      query() {
        return {
          inventory: this.inventoryId,
          add: ['itemType', 'variantType', 'modifierType', 'comboType', 'supplementType', 'customType']
        }
      }
    },

    async mounted() {
      await this.refresh()
    },

    methods: {

      async refresh() {
        this.outOfStock = [];
        this.originalOutOfStock = [];
        let [oosReq, snoozeReq] = await Promise.all([
          Core.request('GET', `/api/inventory/${this.companyNameCanonical}/orderables`, { augment: 'short', outOfStock: true }),
          Core.request('GET', '/api/item_snoozes', { company: this.companyId, active: true, perPage: -1 })
        ]);
        var snoozes = snoozeReq.data.map(s => {
          return {
            id: s.item.id,
            snoozeId: s.id,
            name: s.item.name,
            type: s.item.type,
            outOfStock: s.item.outOfStock,
            fromTime: DateTime.fromFormat(s.fromTime, this.dateFormat).toISO(),
            toTime: DateTime.fromFormat(s.toTime, this.dateFormat).toISO()
          };
        });
        for (let o of oosReq) {
          o.outOfStock = true;
        }
        this.outOfStock = [...oosReq, ...snoozes];
        this.originalOutOfStock = JSON.parse(JSON.stringify(this.outOfStock));
      },

      async deleteItem(item) {
        this.outOfStock = this.outOfStock.filter(e => e !== item);
      },

      async deleteSnooze(id) {
        this.$store.commit('showSpinner', true);
        try {
          await Core.request('DELETE', `/api/item_snoozes/${id}`);
        } catch (e) {
          toast(this.$t("error.no_server"), "error");
        }
        this.$store.commit('showSpinner', false);
      },

      async deleteOutOfStock(id) {
        this.$store.commit('showSpinner', true);
        try {
          await Core.request("PUT", `/api/inventory/${this.companyNameCanonical}/orderables/${id}`,
            { outOfStock: false });
        } catch (e) {
          toast(this.$t("error.no_server"), "error");
        }
        this.$store.commit('showSpinner', false);
      },

      async addNew() {
        this.outOfStock.push({
          id: null,
          snoozeId: null,
          name: null,
          type: null,
          outOfStock: true,
          fromTime: null,
          toTime: null
        });
      },

      async saveOutOfStock(item) {
        let data = {
          id: item.id,
          outOfStock: item.outOfStock
        };
        await Core.request("PUT", `/api/inventory/${this.companyNameCanonical}/orderables/${data.id}`, data)
      },

      async saveSnooze(item) {
        let data = {
          id: item.snoozeId,
          fromTime: item.fromTime,
          toTime: item.toTime,
          item: item.id
        }
        if (!data.id) {
          data.company = this.companyId;
        }
        await Core.request(
          (item.snoozeId ? "PUT" : "POST"),
          (item.snoozeId ? `/api/item_snoozes/${data.id}` : '/api/item_snoozes'),
          data);
      },

      async save() {
        // Compile items to be removed
        let itemsToRemove = this.originalOutOfStock
          .filter(e => {
            let current = this.outOfStock.find(o => o.id === e.id);
            return !current || current.outOfStock !== e.outOfStock;
          })
          .filter(e => !!e.id);

        // Compile items to be added
        let itemsToAdd = this.outOfStock
          .filter(e => {
            let original = this.originalOutOfStock.find(o => o.id === e.id);
            return !original || original.outOfStock !== e.outOfStock;
          })
          .filter(e => !!e.id);

        // Compile items to be edited
        let itemsToEdit = this.outOfStock
          .filter(e => {
            let original = this.originalOutOfStock.find(o => o.id === e.id);
            return original && original.outOfStock === e.outOfStock && JSON.stringify(original) !== JSON.stringify(e);
          });
        if (itemsToRemove.length === 0 && itemsToAdd.length === 0 && itemsToEdit.length === 0) {
          return;
        }

        showSpinner();
        // Remove items
        for (let item of itemsToRemove) {
          if (item.outOfStock) {
            await this.deleteSnooze(item.snoozeId);
          } else {
            await this.deleteOutOfStock(item.id);
          }
        }
        // Add and edit items
        for (let item of [...itemsToAdd, ...itemsToEdit]) {
          if (item.outOfStock) {
            await this.saveOutOfStock(item);
          } else {
            await this.saveSnooze(item);
          }
        }
        hideSpinner();

        await this.refresh();
      }

    }
  }
</script>

<style lang="scss" scoped>
  .snooze {
    padding: 12px;
    box-shadow: 0 2px 4px 1px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    display: flex;
    align-items: flex-end;

    &:not(:last-child) {
      margin-bottom: 12px;
    }

    label {
      margin-bottom: 4px;
    }

    .grow {
      flex-grow: 1;
    }

    .section {
      &:not(:last-child) {
        margin-right: 16px;
      }
      
      &.middle {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: -10px;
        .section {
          margin-bottom: 10px;
        }
      }

      .icon,
      .action {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .icon {
        background-color: #F5F5F5;
        fill: #CCCCCC;
      }

      .action {
        background-color: white;
        fill: var(--primary-color);
        cursor: pointer;
      }
    }
  }

  .addSnooze {
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color);
    border: 2px dashed #E6E6E6;
    border-radius: 8px;
    cursor: pointer;

    .svgicon {
      margin-right: 8px;
      fill: var(--primary-color);
    }
  }
</style>

<style lang="scss">
  #itemSnoozes input,
  #itemSnoozes select {
    font-size: 14px;
    padding: 9.5px 8px;

    &+button {
      top: 6px;
    }
    
    &.vdatetime-input {
      width: 150px;
    }
  }
  #itemSnoozes .ajax-select input {
    min-width: 175px;
  }
</style>